/**
 * Country → locale hint from Worker /api/geo-locale (CF-IPCountry).
 * Applied only when the user has no stored locale preference.
 */

import type { Locale } from '../i18n/strings'
import { isLocale } from '../i18n/strings'

/** ISO 3166-1 alpha-2 → app locale. Unlisted countries → null (use navigator). */
export function localeFromCountry(country: string | null | undefined): Locale | null {
  if (!country) return null
  const c = country.trim().toUpperCase()
  if (!c || c === 'XX' || c === 'T1') return null
  if (c === 'KR') return 'ko'
  if (c === 'JP') return 'ja'
  if (c === 'BR') return 'pt-BR'
  if (c === 'ES' || c === 'MX' || c === 'AR') return 'es'
  if (c === 'RU') return 'ru'
  if (c === 'DE' || c === 'AT') return 'de'
  if (c === 'FR' || c === 'BE') return 'fr'
  if (c === 'TW' || c === 'HK') return 'zh-TW'
  return null
}

export type GeoLocaleResponse = {
  country: string | null
  locale: Locale | null
}

/** Fetch geo locale hint; returns null on network/parse failure. */
export async function fetchGeoLocale(): Promise<GeoLocaleResponse | null> {
  try {
    const res = await fetch('/api/geo-locale', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
    if (!res.ok) return null
    const data = (await res.json()) as { country?: unknown; locale?: unknown }
    const country =
      typeof data.country === 'string' && data.country.trim()
        ? data.country.trim().toUpperCase()
        : null
    let locale: Locale | null = null
    if (isLocale(data.locale)) locale = data.locale
    else locale = localeFromCountry(country)
    return { country, locale }
  } catch {
    return null
  }
}
